import { useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { socketService, WsHelpRequest } from '../../services/socket.service';
import { helpRequestService } from '../../services/help-request.service';
import { useAuth } from '../../hooks/useAuth';
import { browserNotifications } from '../../utils/browserNotifications';
import HelpNotificationCard, { HelpNotification } from './HelpNotificationCard';

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 20_000;

/**
 * Manages help request notification cards for instructors.
 * Mounted at the App level — renders only when user is an instructor
 * and a sessionId is extractable from the current URL.
 * Piggybacks on the existing WebSocket connection established by
 * useSessionSocket on the active page.
 */
export default function HelpNotificationManager() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<HelpNotification[]>([]);
  const [queue, setQueue] = useState<HelpNotification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  // Extract sessionId from URL — works for all instructor session routes
  const sessionId = extractSessionId(location.pathname);

  // Only render for instructors with an active session page
  const isActive = user?.role === 'instructor' && !!sessionId;

  // Start auto-dismiss timer for a notification
  const startTimer = useCallback((id: string) => {
    const existing = timersRef.current.get(id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      timersRef.current.delete(id);
    }, AUTO_DISMISS_MS);
    timersRef.current.set(id, timer);
  }, []);

  // Pause auto-dismiss (on hover / interaction)
  const clearTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  // Dismiss a notification
  const handleDismiss = useCallback((id: string) => {
    clearTimer(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, [clearTimer]);

  // Quick Reply handler
  const handleQuickReply = useCallback(async (helpRequestId: string, message: string): Promise<boolean> => {
    try {
      await helpRequestService.addMessage(helpRequestId, message);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Promote queued items when a visible slot opens
  useEffect(() => {
    if (notifications.length < MAX_VISIBLE && queue.length > 0) {
      setQueue((prev) => {
        const [next, ...rest] = prev;
        if (next) {
          setNotifications((n) => [...n, next]);
          startTimer(next.id);
        }
        return rest;
      });
    }
  }, [notifications.length, queue.length, startTimer]);

  // Subscribe to help-request events via the shared socket
  useEffect(() => {
    if (!isActive) return;

    const unsub = socketService.onHelpRequest((data: WsHelpRequest) => {
      // Only show for new (open) help requests, not status changes
      if (data.status !== 'open') return;
      // Don't show notifications for instructor's own actions
      if (data.studentId === user?.id) return;

      const notif: HelpNotification = {
        id: `help-${data.id}-${Date.now()}`,
        helpRequestId: data.id,
        sessionId: data.sessionId,
        studentName: data.studentName || 'A student',
        stepTitle: data.stepTitle,
        stepOrderIndex: data.stepOrderIndex,
        message: data.message || 'Needs help',
        timestamp: Date.now(),
      };

      // Browser notification when tab not focused
      if (document.hidden || !document.hasFocus()) {
        const stepNum = data.stepOrderIndex != null ? `Step ${data.stepOrderIndex + 1}` : '';
        browserNotifications.show(
          `🙋 ${notif.studentName}${stepNum ? ` — ${stepNum}` : ''}`,
          {
            body: notif.message,
            tag: `help-${data.id}`,
            onClick: () => {
              navigate(`/instructor/session/${data.sessionId}/help`);
            },
          },
        );
      }

      // Add to visible or queue
      if (notificationsRef.current.length < MAX_VISIBLE) {
        setNotifications((prev) => [...prev, notif]);
        startTimer(notif.id);
      } else {
        setQueue((prev) => [...prev, notif]);
      }
    });

    return () => {
      unsub();
    };
  }, [isActive, user?.id, navigate, startTimer]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  if (!isActive || (notifications.length === 0 && queue.length === 0)) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column-reverse',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {notifications.map((notif, index) => (
        <HelpNotificationCard
          key={notif.id}
          notification={notif}
          index={index}
          onDismiss={handleDismiss}
          onQuickReply={handleQuickReply}
        />
      ))}
    </Box>
  );
}

/** Extract sessionId from instructor routes */
function extractSessionId(pathname: string): string | null {
  // /instructor/session/:id/present
  // /instructor/session/:id/help
  // /instructor/session/:id
  // /instructor/live/:id
  const sessionMatch = pathname.match(/\/instructor\/session\/([^/]+)/);
  if (sessionMatch) return sessionMatch[1];

  const liveMatch = pathname.match(/\/instructor\/live\/([^/]+)/);
  if (liveMatch) return liveMatch[1];

  return null;
}
